import React, { useContext } from 'react'
import { useDispatch } from 'react-redux'
import {
    authSiteLogout,
    persistOriginUrl,
    getLoginUrl,
    clearAuthTokens,
    getSubscribeUrl,
    getAuthParams,
} from 'common/authentication/helpers'
import { ConfigurationContext } from 'common/context-providers/configuration-context'
import { LoggerContext } from 'component-library/diagnostics/LoggerContext'
import { isServerEnvironment } from 'common/environment'
import { useSectionMeta } from 'common/routing/use-section-meta'
import { authLogout } from 'common/data/authentication/reducer'
import { IsHydrating } from 'common/context-providers/is-hydrating'
import { utm_medium_breach } from 'common/ssw'

export interface SubscribeClickEventOptions {
    articleId?: string
    packagePath?: string
    breachCreativeName: string
    breachDeal: string
}

export type AuthenticationProviderProps = {
    children: AuthenticationProviderChildren
    onEvent: (event: any) => void
}

export type AuthenticationProviderChildren = (props: {
    onLoginClick(): void
    onLogoutClick(): void
    onSubscribeClick(): void
    createBreachSubscribeClickEvent(options: SubscribeClickEventOptions): () => void
}) => React.ReactElement

export const AuthenticationProvider: React.FC<AuthenticationProviderProps> = ({
    children,
    onEvent,
}) => {
    const dispatch = useDispatch()
    const config = useContext(ConfigurationContext)
    const logger = useContext(LoggerContext)
    const sectionMeta = useSectionMeta()
    const isHydrating = React.useContext(IsHydrating)

    const hostname = sectionMeta.Hostname ? `https://${sectionMeta.Hostname}` : config.publicUrl
    const pageUrl =
        isServerEnvironment() || isHydrating ? hostname : `${hostname}${window.location.pathname}`

    const onLoginClick = () => {
        persistOriginUrl()

        const loginUrl = getLoginUrl(config, sectionMeta)
        window.location.href = loginUrl
    }

    const onLogoutClick = async () => {
        dispatch(
            authLogout({
                onEvent,
            })
        )
        await authSiteLogout(config, logger)
        clearAuthTokens()
    }

    const onSubscribeClick = () => {
        persistOriginUrl()
        const auth = getAuthParams(config, sectionMeta)

        const subscribeLink = getSubscribeUrl({
            config,
            pageUrl,
            source: sectionMeta.Hostname,
            auth,
        })
        window.location.href = subscribeLink
    }

    const createBreachSubscribeClickEvent = (options: SubscribeClickEventOptions) => {
        const { articleId, packagePath, breachCreativeName, breachDeal } = options
        const auth = getAuthParams(config, sectionMeta)

        const subscribeLink = getSubscribeUrl({
            config,
            packagePath,
            pageUrl,
            auth,
            source: sectionMeta.Hostname,
            campaign: breachCreativeName,
            medium: utm_medium_breach,
            additionalParams: {
                utm_term: breachDeal,
                utm_content: articleId,
            },
        })

        return () => {
            persistOriginUrl()
            window.location.href = subscribeLink
        }
    }

    return children({
        onLoginClick,
        onLogoutClick,
        onSubscribeClick,
        createBreachSubscribeClickEvent,
    })
}
